import React, { useState } from 'react';
import { Bell, Tag, Calendar } from 'lucide-react';
import styles from './Announcements.module.css';
import bgPattern from '../../../../Assets/BRANDING-BG.png';
import { Header } from '../header/Header';
import { DashboardSidebar } from '../DashboardSidebar/DashboardSidebar';

const ANNOUNCEMENTS_DATA = [
    {
        id: 1,
        priority: 'high',
        status: 'Active',
        category: 'Maintenance Advisory',
        title: 'Scheduled Maintenance: Network Infrastructure Upgrade',
        description: 'We will be performing a scheduled network infrastructure upgrade on January 20, 2026 from 02:00 to 06:00 UTC. Expect brief connectivity interruptions during this window.',
        publishedDate: 'Jan 14, 2026',
        validUntil: 'Jan 21, 2026'
    },
    {
        id: 2,
        priority: 'normal',
        status: 'Active',
        category: 'New Feature Announcement',
        title: 'New Feature: Enhanced Monitoring Dashboard',
        description: 'We have deployed new monitoring capabilities to your dashboard. Enhanced metrics and real-time alerting are now available for all managed services.',
        publishedDate: 'Jan 14, 2026',
        validUntil: 'Feb 10, 2026'
    },
    {
        id: 3,
        priority: 'normal',
        status: 'Active',
        category: 'Report Published',
        title: 'Q4 2025 Service Performance Report Available',
        description: 'Your quarterly service performance report for Q4 2025 is now available in the QBR & Shared Materials section.',
        publishedDate: 'Jan 14, 2026',
        validUntil: 'Jan 31, 2026'
    }
];

const EXPIRED_ANNOUNCEMENTS_DATA = [
    {
        id: 4,
        status: 'Expired',
        category: 'Migration',
        title: 'Completed : Database Migration',
        description: 'The database migration has been successfully completed. All services are operating normally.',
        publishedDate: 'Jan 14, 2026'
    },
    {
        id: 5,
        status: 'Expired',
        category: 'Support Schedule',
        title: 'Holiday Support Schedule',
        description: 'Please note our modified support hours during the holiday period from December 24 to January 1.',
        publishedDate: 'Jan 14, 2026'
    }
];

const AnnouncementCard = ({ announcement }) => {
    return (
        <div className={`${styles.card} ${announcement.priority === 'high' ? styles.highPriority : ''}`}>
            <div className={styles.cardHeader}>
                <div className={styles.statusContainer}>
                    <div className={`${styles.bellIcon} ${announcement.priority === 'high' ? styles.bellOrange : styles.bellGray}`}>
                        <Bell size={16} />
                    </div>
                    <div className={styles.statusBadge}>
                        <span className={styles.statusText}>{announcement.status}</span>
                    </div>
                </div>
                <div className={styles.metaInfo}>
                    <div className={styles.metaItem}>
                        <Tag size={14} />
                        <span>{announcement.category}</span>
                    </div>
                    <div className={styles.metaItem}>
                        <Calendar size={14} />
                        <span>Published {announcement.publishedDate}</span>
                    </div>
                </div>
            </div>

            <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{announcement.title}</h3>
                <p className={styles.cardDescription}>{announcement.description}</p>
            </div>

            <div className={styles.cardFooter}>
                <span className={styles.validUntil}>Valid until {announcement.validUntil}</span>
            </div>
        </div>
    );
};

const ExpiredAnnouncementCard = ({ announcement }) => {
    return (
        <div className={`${styles.card} ${styles.expiredCard}`}>
            <div className={styles.cardHeader}>
                <div className={styles.statusContainer}>
                    <div className={`${styles.bellIcon} ${styles.bellGray}`}>
                        <Bell size={16} />
                    </div>
                    <div className={`${styles.statusBadge} ${styles.expiredBadge}`}>
                        <span className={styles.statusText}>{announcement.status}</span>
                    </div>
                </div>
                <div className={styles.metaInfo}>
                    <div className={styles.metaItem}>
                        <Tag size={14} />
                        <span>{announcement.category}</span>
                    </div>
                    <div className={styles.metaItem}>
                        <Calendar size={14} />
                        <span>Published {announcement.publishedDate}</span>
                    </div>
                </div>
            </div>

            <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{announcement.title}</h3>
                <p className={styles.cardDescription}>{announcement.description}</p>
            </div>
        </div>
    );
};

export const Announcements = () => {
    const activeCount = ANNOUNCEMENTS_DATA.filter(a => a.status === 'Active').length;
    const expiredCount = EXPIRED_ANNOUNCEMENTS_DATA.length;
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.headerWrapper}>
                <Header
                    onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                />
            </div>

            <div className={styles.mainWrapper}>
                <DashboardSidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                />

                <div className={styles.container} style={{ backgroundImage: `url(${bgPattern})` }}>
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <h1 className={styles.title}>Announcements</h1>
                            <p className={styles.subtitle}>
                                Official communications and service advisories for your managed environment.
                            </p>
                        </div>
                        <div className={styles.headerRight}>
                            <span className={styles.lastUpdated}>
                                Last updated: <strong>Jan 15, 2026 at 09:00 AM</strong>
                            </span>
                        </div>
                    </div>

                    <div className={styles.sectionHeader}>
                        <div className={styles.activeIndicator} />
                        <h2 className={styles.sectionTitle}>Active Communications ({activeCount})</h2>
                    </div>

                    <div className={styles.cardsContainer}>
                        {ANNOUNCEMENTS_DATA.map((announcement) => (
                            <AnnouncementCard key={announcement.id} announcement={announcement} />
                        ))}
                    </div>

                    <div className={`${styles.sectionHeader} ${styles.expiredSection}`}>
                        <div className={styles.expiredIndicator} />
                        <h2 className={styles.sectionTitle}>Expired Communications ({expiredCount})</h2>
                    </div>

                    <div className={styles.cardsContainer}>
                        {EXPIRED_ANNOUNCEMENTS_DATA.map((announcement) => (
                            <ExpiredAnnouncementCard key={announcement.id} announcement={announcement} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Announcements;
